import * as React from "react";
import { MoreHorizontal } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

/** A record-kind glyph: a Lucide icon, or a mark that draws itself (Google Drive's). */
type RowIcon = React.ComponentType<{ className?: string; strokeWidth?: number }>;

/** A decorative 36 px record-kind tile. The adjacent title names the record. */
function IconTile({ icon: Icon, className }: { icon: RowIcon; className?: string }) {
  return (
    <span data-slot="icon-tile" aria-hidden="true" className={cn("grid size-9 shrink-0 place-items-center rounded-control bg-accent text-foreground-soft", className)}>
      <Icon className="size-[1.125rem]" strokeWidth={1.6} />
    </span>
  );
}

/**
 * A record inside a list, with one content column and a bounded trailing area.
 * Callers retain exact identities, links, state copy, order, and permissions.
 * Controls remain siblings of the record title, never nested in its link.
 *
 * `detail` is what the record opens in place (its editor, say): it runs under
 * the title and the trailing acts together, so a narrow screen does not hand
 * it only the title's share of the row.
 */
function ListRow({
  icon,
  children,
  trailing,
  detail,
  className,
  ...props
}: React.ComponentProps<"li"> & { icon: RowIcon; trailing?: React.ReactNode; detail?: React.ReactNode }) {
  return (
    <li data-slot="list-row" className={cn("grid min-w-0 grid-cols-[2.25rem_minmax(0,1fr)_auto] items-center gap-x-3 border-t border-rule py-2.5 ps-5 pe-3", className)} {...props}>
      <IconTile icon={icon} />
      <div data-slot="list-row-content" className="min-w-0 [overflow-wrap:anywhere]">{children}</div>
      <div data-slot="list-row-trailing" className="flex min-w-0 flex-wrap items-center justify-end gap-2">{trailing}</div>
      {detail && <div data-slot="list-row-detail" className="col-start-2 col-end-4 min-w-0">{detail}</div>}
    </li>
  );
}

/** The record's own permitted actions, with a named and keyboard-native menu. */
function ListRowMenu({ label, children, className, ...props }: { label: string; children: React.ReactNode } & React.ComponentProps<typeof DropdownMenuContent>) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghost" size="icon-sm" aria-label={label} className="size-11 -m-[0.4375rem] text-foreground-soft">
          <MoreHorizontal aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={cn("w-56", className)} {...props}>{children}</DropdownMenuContent>
    </DropdownMenu>
  );
}

export { IconTile, ListRow, ListRowMenu };
